import { useCallback, useEffect, useRef, useState } from "react";
import { addVariantToCart } from "../lib/cart";
import { discountedMoney, formatMoney } from "../lib/format";
import { useProductDetail } from "../hooks/useProductDetail";
import { useRelatedProducts } from "../hooks/useRelatedProducts";
import { ProductGrid } from "./ProductGrid";
import { useSnackbar } from "./Snackbar";
import type { PocaConfig, ProductImage, ProductVariant } from "../types";

type Props = {
  handle: string;
  config: PocaConfig;
  onBack: () => void;
  /** 추천 카드 클릭 시 해당 상품 상세로 인페이지 이동 */
  onNavigate?: (handle: string) => void;
};

/* ================================================================
   Helpers
   ================================================================ */
function isDefaultVariant(variants: ProductVariant[]): boolean {
  return variants.length === 1 && variants[0].title === "Default Title";
}

/* ================================================================
   SVG Icons
   ================================================================ */
const ChevronLeftIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const PinterestIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
  </svg>
);


/* ================================================================
   Share Buttons
   ================================================================ */
function ShareButtons({ title, imageUrl }: { title: string; imageUrl: string }) {
  const pageUrl = window.location.href;

  const openPopup = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=500");
  };

  return (
    <div className="poca-detail__share">
      <span className="poca-detail__share-label">Share</span>
      <div className="poca-detail__share-btns">
        <button
          type="button"
          className="poca-detail__share-btn poca-detail__share-btn--fb"
          aria-label="Share on Facebook"
          onClick={() => openPopup(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`)}
        >
          <FacebookIcon />
        </button>

        <button
          type="button"
          className="poca-detail__share-btn poca-detail__share-btn--x"
          aria-label="Share on X"
          onClick={() => openPopup(`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(title)}`)}
        >
          <XIcon />
        </button>

        <button
          type="button"
          className="poca-detail__share-btn poca-detail__share-btn--pin"
          aria-label="Pin on Pinterest"
          onClick={() => openPopup(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(title)}`)}
        >
          <PinterestIcon />
        </button>
      </div>
    </div>
  );
}

/* ================================================================
   Image Lightbox
   ================================================================ */
function ImageLightbox({
  images,
  activeIndex,
  onClose,
  onPrev,
  onNext,
}: {
  images: ProductImage[];
  activeIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const touchStartXRef = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = touchStartXRef.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) onNext();
      else onPrev();
    }
  };

  const img = images[activeIndex];
  if (!img) return null;

  return (
    <div
      className="poca-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button type="button" className="poca-lightbox__close" onClick={onClose} aria-label="Close">
        <CloseIcon />
      </button>

      {images.length > 1 && (
        <>
          <button type="button" className="poca-lightbox__nav poca-lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Previous image">
            <ChevronLeftIcon size={24} />
          </button>
          <button type="button" className="poca-lightbox__nav poca-lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Next image">
            <ChevronRightIcon size={24} />
          </button>
        </>
      )}

      <div className="poca-lightbox__img-wrap" onClick={(e) => e.stopPropagation()}>
        <img src={img.url} alt={img.altText ?? ""} width={img.width} height={img.height} />
      </div>

      {images.length > 1 && (
        <p className="poca-lightbox__counter" aria-live="polite">
          {activeIndex + 1} / {images.length}
        </p>
      )}
    </div>
  );
}

/* ================================================================
   Main ProductDetail component
   ================================================================ */
export function ProductDetail({ handle, config, onBack, onNavigate }: Props) {
  const { product, loading, error } = useProductDetail(config, handle);
  const { products: relatedProducts } = useRelatedProducts(
    config,
    product?.handle ?? null,
    product?.tags ?? []
  );
  const { showSnackbar } = useSnackbar();

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(false);
  const touchStartXRef = useRef<number>(0);

  // Reset quantity when variant changes
  useEffect(() => {
    setQuantity(1);
  }, [selectedVariantId]);

  const images: ProductImage[] = product
    ? product.images.length > 0
      ? product.images
      : product.featuredImage
        ? [product.featuredImage]
        : []
    : [];

  const imageCount = images.length;

  const goPrev = useCallback(() => {
    setActiveImageIndex((i) => (i - 1 + imageCount) % (imageCount || 1));
  }, [imageCount]);

  const goNext = useCallback(() => {
    setActiveImageIndex((i) => (i + 1) % (imageCount || 1));
  }, [imageCount]);

  // Keyboard: Esc = close lightbox, ←/→ = navigate
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && lightboxOpen) { setLightboxOpen(false); return; }
      if (imageCount <= 1) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowLeft")  { e.preventDefault(); goPrev(); }
      if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [imageCount, lightboxOpen, goPrev, goNext]);

  const handleSliderTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleSliderTouchEnd = (e: React.TouchEvent) => {
    const delta = touchStartXRef.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) goNext();
      else goPrev();
    }
  };

  if (loading) return <ProductDetailSkeleton onBack={onBack} />;

  if (error || !product) {
    return (
      <div className="poca-detail">
        <button type="button" className="poca-detail__back" onClick={onBack}>
          <ChevronLeftIcon /> Back to Photocards
        </button>
        <div className="poca-alert poca-alert--error" role="alert">
          {error ?? "Product not found."}
        </div>
      </div>
    );
  }

  const showVariants = !isDefaultVariant(product.variants);
  const resolvedVariantId = selectedVariantId ?? product.variants[0]?.id ?? null;
  const selectedVariant =
    product.variants.find((v) => v.id === resolvedVariantId) ??
    product.variants[0] ?? null;

  const canBuy =
    config.enableAddToCart &&
    product.availableForSale &&
    selectedVariant != null &&
    selectedVariant.availableForSale;

  const basePrice = selectedVariant
    ? selectedVariant.price
    : product.priceRange.minVariantPrice;

  // 스토어 전역 할인(테마 에디터 설정). 켜져 있으면 현재가를 긋고 할인가를 보여준다.
  // compare-at 세일과 겹치면 표기가 이중으로 꼬여서 전역 할인이 우선한다.
  const storeDiscount = config.discountPercent > 0;
  const displayPrice = storeDiscount
    ? discountedMoney(basePrice, config.discountPercent)
    : basePrice;

  const compareAtPrice = storeDiscount
    ? basePrice
    : (selectedVariant?.compareAtPrice ?? product.compareAtPriceRange.minVariantPrice);

  const showCompareAt = storeDiscount
    ? true
    : compareAtPrice != null &&
      Number.parseFloat(compareAtPrice.amount) > Number.parseFloat(displayPrice.amount);

  const handleAdd = async () => {
    if (!canBuy || busy || !selectedVariant) return;
    setBusy(true);
    const result = await addVariantToCart(config, selectedVariant.id, quantity);
    setBusy(false);
    if (result.ok) {
      setAddedFeedback(true);
      window.setTimeout(() => setAddedFeedback(false), 2000);
    } else {
      showSnackbar(result.message ?? "Could not add to cart");
    }
  };

  const firstImageUrl = images[0]?.url ?? "";

  return (
    <article className="poca-detail">
      <button type="button" className="poca-detail__back" onClick={onBack}>
        <ChevronLeftIcon /> Back to Photocards
      </button>

      <div className="poca-detail__layout">
        {/* ── Gallery ── */}
        <div className="poca-detail__gallery">
          <p className="poca-detail__scan-note" role="note">
            <svg className="poca-detail__scan-note-icon" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>
              Note: The lines on the pictures are from the scanner, not the card. The card itself is in pristine condition.
            </span>
          </p>
          <div
            className="poca-detail__slider"
            onTouchStart={handleSliderTouchStart}
            onTouchEnd={handleSliderTouchEnd}
          >
            <div
              className="poca-detail__slider-track"
              style={{ transform: `translateX(-${activeImageIndex * 100}%)` }}
            >
              {images.length > 0 ? (
                images.map((img, i) => (
                  <div
                    key={`${img.url}-${i}`}
                    className="poca-detail__slide"
                    onClick={() => setLightboxOpen(true)}
                    role="button"
                    tabIndex={i === activeImageIndex ? 0 : -1}
                    onKeyDown={(e) => e.key === "Enter" && setLightboxOpen(true)}
                    aria-label="Enlarge image"
                  >
                    <img
                      src={img.url}
                      alt={img.altText ?? product.title}
                      width={img.width}
                      height={img.height}
                      loading={i === 0 ? "eager" : "lazy"}
                      draggable={false}
                    />
                  </div>
                ))
              ) : (
                <div className="poca-detail__slide poca-detail__slide--empty">No image</div>
              )}
            </div>

            {imageCount > 1 && (
              <>
                <button type="button" className="poca-detail__slider-btn poca-detail__slider-btn--prev"
                  onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Previous image">
                  <ChevronLeftIcon size={20} />
                </button>
                <button type="button" className="poca-detail__slider-btn poca-detail__slider-btn--next"
                  onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Next image">
                  <ChevronRightIcon size={20} />
                </button>
              </>
            )}

            {imageCount > 0 && (
              <button type="button" className="poca-detail__zoom-btn"
                onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }} aria-label="Enlarge image">
                <ZoomInIcon />
              </button>
            )}

            {imageCount > 1 && (
              <span className="poca-detail__counter" aria-live="polite">
                {activeImageIndex + 1} / {imageCount}
              </span>
            )}

            {!product.availableForSale && (
              <span className="poca-card__badge poca-card__badge--sold">Sold out</span>
            )}
          </div>

          {imageCount > 1 && (
            <div className="poca-detail__thumbs">
              {images.map((img, i) => (
                <button key={`thumb-${i}`} type="button"
                  className={`poca-detail__thumb${i === activeImageIndex ? " is-active" : ""}`}
                  onClick={() => setActiveImageIndex(i)} aria-label={`Image ${i + 1}`}>
                  <img src={img.url} alt={img.altText ?? `Image ${i + 1}`}
                    width={img.width} height={img.height} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Product info ── */}
        <div className="poca-detail__info">
          <h1 className="poca-detail__title">{product.title}</h1>

          {/* cbb-ignored-price: Currency Converter Plus 앱의 가격 재파싱 방지 (ProductCard 참고) */}
          <div className="poca-detail__prices cbb-ignored-price">
            <span className={`poca-detail__price${showCompareAt ? " poca-detail__price--sale" : ""}`}>
              {formatMoney(displayPrice, config.locale)}
            </span>
            {showCompareAt && compareAtPrice && (
              <span className="poca-detail__compare">{formatMoney(compareAtPrice, config.locale)}</span>
            )}
            {showCompareAt && (
              <span className="poca-detail__sale-badge">
                {storeDiscount
                  ? `${config.discountLabel} -${Math.round(config.discountPercent)}%`
                  : "Sale"}
              </span>
            )}
          </div>

          {/* 수량 할인 안내. 바로 아래 수량 선택이 있어서 여기 두면 "더 담으면 싸진다" 가 바로 이어진다.
              프로모션 기간에만 여는 배너라 테마 에디터 토글(showBulkDeal)로 숨겼다 다시 켤 수 있다. */}
          {config.showBulkDeal && (
            <div className="poca-bulk-deal" role="note" aria-label="Bulk discount">
              <p className="poca-bulk-deal__title">Poca Vault Exclusive Sale!</p>
              <ul className="poca-bulk-deal__tiers">
                <li className="poca-bulk-deal__tier">
                  <span className="poca-bulk-deal__qty">5–9 cards</span>
                  <span className="poca-bulk-deal__off">5% OFF</span>
                </li>
                <li className="poca-bulk-deal__tier">
                  <span className="poca-bulk-deal__qty">10–19 cards</span>
                  <span className="poca-bulk-deal__off">10% OFF</span>
                </li>
                <li className="poca-bulk-deal__tier poca-bulk-deal__tier--best">
                  <span className="poca-bulk-deal__qty">20+ cards</span>
                  <span className="poca-bulk-deal__off">20% OFF</span>
                </li>
              </ul>
            </div>
          )}

          {showVariants && (
            <div className="poca-detail__variants">
              <p className="poca-detail__variants-label">
                Option
                {selectedVariant && (
                  <span className="poca-detail__variants-selected">: {selectedVariant.title}</span>
                )}
              </p>
              <div className="poca-detail__variant-list">
                {product.variants.map((v) => (
                  <button key={v.id} type="button"
                    className={[
                      "poca-detail__variant-btn",
                      v.id === resolvedVariantId ? "is-active" : "",
                      !v.availableForSale ? "is-soldout" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => setSelectedVariantId(v.id)}
                    disabled={!v.availableForSale}>
                    {v.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity → Add to Cart (stacked) */}
          {config.enableAddToCart && (
            <div className="poca-detail__buy-section">
              <div className="poca-detail__qty-row">
                <span className="poca-detail__qty-label">Quantity</span>
                <div className="poca-detail__qty">
                  <button type="button" className="poca-detail__qty-btn"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1} aria-label="Decrease quantity">
                    −
                  </button>
                  <span className="poca-detail__qty-num" aria-live="polite">{quantity}</span>
                  <button type="button" className="poca-detail__qty-btn"
                    onClick={() => setQuantity((q) => q + 1)}
                    aria-label="Increase quantity">
                    +
                  </button>
                </div>
              </div>

              <button type="button" className="poca-detail__atc"
                disabled={!canBuy || busy} onClick={handleAdd}>
                {!product.availableForSale
                  ? "Sold Out"
                  : busy
                    ? "Adding to Cart…"
                    : addedFeedback
                      ? "✓ Added to Cart!"
                      : "Add to Cart"}
              </button>
            </div>
          )}

          {/* Share */}
          <div className="poca-detail__actions">
            <ShareButtons title={product.title} imageUrl={firstImageUrl} />
          </div>

          <a href={`/products/${product.handle}`} className="poca-detail__shopify-link"
            target="_blank" rel="noopener noreferrer">
            View full product page ↗
          </a>

          {product.descriptionHtml && (
            <div className="poca-detail__description"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Shopify product description HTML
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
          )}
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <section className="poca-detail__related" aria-label="Recommended for you">
          <h2 className="poca-detail__related-title">Recommended for you</h2>
          <ProductGrid
            products={relatedProducts}
            config={config}
            onNavigate={onNavigate}
          />
        </section>
      )}

      {lightboxOpen && (
        <ImageLightbox images={images} activeIndex={activeImageIndex}
          onClose={() => setLightboxOpen(false)} onPrev={goPrev} onNext={goNext} />
      )}
    </article>
  );
}

function ProductDetailSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <article className="poca-detail">
      <button type="button" className="poca-detail__back" onClick={onBack}>
        <ChevronLeftIcon /> Back to Photocards
      </button>
      <div className="poca-detail__layout">
        <div className="poca-detail__gallery">
          <div className="poca-detail__slider poca-skeleton" />
        </div>
        <div className="poca-detail__info">
          <div className="poca-skeleton poca-skeleton--title" />
          <div className="poca-skeleton poca-skeleton--price" />
          <div className="poca-skeleton poca-skeleton--btn" />
        </div>
      </div>
    </article>
  );
}
